import { VesselInfoDetail } from '@app/domain/entities/vessel-info.entity';
import { VesselInfoDTO } from '../dtos/vessel-info.dto';

/**
 * Vessel status codes (`vvStatusInd`), labelled as in the BE `VesselStatusIndEnum`
 * (AL = Incoming, BR = Alongside, UB = Sailed) — the same mapping the landing list uses.
 * Any other value is ignored (no status tag rendered).
 */
const VESSEL_STATUS_LABELS: Record<string, string> = {
  AL: 'Incoming',
  AP: 'Applied',
  BR: 'Alongside',
  CL: 'Closed',
  CX: 'Cancelled',
  UB: 'Sailed',
};

export class VesselInfoMapper {
  toDomain(dto: VesselInfoDTO): VesselInfoDetail {
    return {
      vesselByShiftId: dto.vesselByShiftId,
      vesselName: (dto.vesselName ?? '').trim(),
      voyIn: dto.voyIn ?? null,
      voyOut: dto.voyOut ?? null,
      berthNumber: dto.berthNumber ?? null,
      wm: dto.wm ?? null,
      tags: this._buildTags(dto),
      hasWaterBunkering: !!dto.hasWaterBunkering,
      hasFuelBunkering: !!dto.hasFuelBunkering,
      hasOversideOps: !!dto.hasOversideOps,

      etb: dto.overview?.etb ?? null,
      etu: dto.overview?.etu ?? null,
      atb: dto.overview?.atb ?? null,
      atu: dto.overview?.atu ?? null,
      fat: dto.overview?.fat ?? null,
      firstGB: dto.overview?.firstLift ?? null,
      cod: dto.overview?.cod ?? null,
      col: dto.overview?.col ?? null,
      lat: dto.overview?.lat ?? null,

      draft: dto.overview?.draft ?? null,
      tugOperator: dto.overview?.tugOperator ?? null,
      varcode: dto.overview?.varcode ?? null,
      planBerthSide: dto.overview?.planBerthSide ?? null,
      actBerthSide: dto.overview?.actBerthSide ?? null,
      protrusion: dto.overview?.protrusion ?? null,
      imHauling: dto.overview?.hauling ?? null,
      tandemLift: dto.overview?.tandemLift ?? null,
      floatingCrane: dto.overview?.floatingCrane ?? null,
      waterSupply: dto.overview?.hasWaterBunkering ?? null,
      dangerousCargo: dto.overview?.dangerousCargo ?? null,
      loa: dto.overview?.loa ?? null,
      specialActivities: dto.overview?.specialActivity ?? null,
      newWm: dto.overview?.newWm ?? null,
      tow: dto.overview?.hasTow ?? null,

      agentName: dto.agent?.agentName ?? null,
      agentContact: dto.agent?.agentContact?.offTel1 ?? null,

      bunkerSupplier: dto.waterBunker?.bunkerSupplier ?? null,
      bunkerDateTime: dto.waterBunker?.bunkerDateTime ?? null,
      bunkerDuration: dto.waterBunker?.bunkerDurationHr ?? null,
      bunkerQuantity: dto.waterBunker?.bunkerQuantity ?? null,
      waterQuantity: dto.waterBunker?.waterQuantityTons ?? null,
      waterRequiredDateTime: dto.waterBunker?.requiredWaterTime ?? null,

      stevedoreDetails: (dto.stevedoreDetail || []).map(item => ({
        stevedore: item.stevedore ?? null,
        personName: item.personName ?? null,
        personContact: item.personContact ?? null,
      })),

      templateHotoNote: dto.templateHOTONote?.trim() || null,
    };
  }

  private _buildTags(dto: VesselInfoDTO): string[] {
    const tags: string[] = [];

    // Any Vessel Scheme, upper-cased (same as the landing list and the mobile app's LinerSchemeTag)
    const scheme = this._parseScheme(dto.linerScheme);
    if (scheme) tags.push(scheme.toUpperCase());
    if (dto.isLNG) tags.push('LNG');

    const vesselType = (dto.vesselType ?? '').toUpperCase();
    if (vesselType === 'NOM/PS') {
      tags.push('NOM', 'PS');
    } else if (vesselType === 'NOM' || vesselType === 'PS') {
      tags.push(vesselType);
    }
    // `isPs` flags a PS vessel whatever its `vesselType`
    if (dto.isPs && !tags.includes('PS')) tags.push('PS');

    const statusLabel = dto.vvStatusInd
      ? VESSEL_STATUS_LABELS[dto.vvStatusInd]
      : undefined;
    if (statusLabel) tags.push(statusLabel);

    return tags;
  }

  /** BE sends "" (or the string "null") when there is no linerScheme. */
  private _parseScheme(scheme: string | null | undefined): string | null {
    if (scheme == null) return null;
    const value = String(scheme).trim();
    if (!value || value.toLowerCase() === 'null') return null;
    return value;
  }
}
